import type { Song, YouTubePlayer } from '@/types';
import { PlayerState, ZemerError } from '@/types';

declare global {
  interface Window {
    YT: {
      Player: new (elementId: string, config: any) => YouTubePlayer;
      PlayerState: typeof PlayerState;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

type PlayerEventCallback = (event: { data: number }) => void;
type PlayerErrorCallback = (event: { data: number }) => void;

interface PlayerCallbacks {
  onReady?: () => void;
  onStateChange?: PlayerEventCallback;
  onError?: PlayerErrorCallback;
}

export class YouTubePlayerManager {
  private player: YouTubePlayer | null = null;
  private ready = false;
  private currentSong: Song | null = null;
  private callbacks: PlayerCallbacks = {};

  constructor() {
    this.loadYouTubeAPI();
  }

  private loadYouTubeAPI(): void {
    if (window.YT) {
      this.ready = true;
      return;
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    if (firstScriptTag?.parentNode) {
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    } else {
      document.head.appendChild(tag);
    }
  }

  initialize(elementId: string, callbacks: PlayerCallbacks): Promise<void> {
    this.callbacks = callbacks;

    return new Promise((resolve, reject) => {
      // Timeout after 10 seconds
      const timeout = setTimeout(() => {
        reject(new ZemerError('YouTube API failed to load', 'PLAYER_TIMEOUT'));
      }, 10000);

      const initPlayer = () => {
        clearTimeout(timeout);
        try {
          this.player = new window.YT.Player(elementId, {
            height: '1',
            width: '1',
            playerVars: {
              playsinline: 1,
              controls: 0,
              autoplay: 1,
              enablejsapi: 1,
            },
            events: {
              onReady: () => {
                this.ready = true;
                this.callbacks.onReady?.();
                resolve();
              },
              onStateChange: (e: any) => {
                this.callbacks.onStateChange?.(e);
                this.updateMediaSession();
              },
              onError: (e: any) => {
                this.callbacks.onError?.(e);
              },
            },
          });
        } catch (error) {
          clearTimeout(timeout);
          reject(new ZemerError('Failed to initialize player', 'PLAYER_INIT_ERROR', { error }));
        }
      };

      if (window.YT) {
        initPlayer();
      } else {
        window.onYouTubeIframeAPIReady = initPlayer;
      }
    });
  }

  loadSong(song: Song, startTime = 0): void {
    if (!this.player || !this.ready) {
      throw new ZemerError('Player not ready', 'PLAYER_NOT_READY');
    }

    this.currentSong = song;
    this.player.loadVideoById(song.videoId, startTime);
    this.updateMediaSession();
  }

  play(): void {
    if (!this.player || !this.ready) return;
    this.player.playVideo();
  }

  pause(): void {
    if (!this.player || !this.ready) return;
    this.player.pauseVideo();
  }

  togglePlay(): void {
    if (!this.player || !this.ready) return;
    const state = this.player.getPlayerState();
    if (state === PlayerState.PLAYING) {
      this.pause();
    } else {
      this.play();
    }
  }

  seek(seconds: number): void {
    if (!this.player || !this.ready) return;
    this.player.seekTo(seconds);
  }

  getCurrentTime(): number {
    if (!this.player || !this.ready) return 0;
    try {
      return this.player.getCurrentTime();
    } catch {
      return 0;
    }
  }

  getDuration(): number {
    if (!this.player || !this.ready) return 0;
    try {
      return this.player.getDuration();
    } catch {
      return 0;
    }
  }

  getState(): number {
    if (!this.player || !this.ready) return PlayerState.UNSTARTED;
    try {
      return this.player.getPlayerState();
    } catch {
      return PlayerState.UNSTARTED;
    }
  }

  isPlaying(): boolean {
    return this.getState() === PlayerState.PLAYING;
  }

  isReady(): boolean {
    return this.ready;
  }

  getCurrentSong(): Song | null {
    return this.currentSong;
  }

  private updateMediaSession(): void {
    if (!('mediaSession' in navigator) || !this.currentSong) return;

    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.currentSong.title,
        artist: this.currentSong.artist,
        album: 'Zemer Music',
        artwork: this.currentSong.thumbnail
          ? [
              {
                src: this.currentSong.thumbnail,
                sizes: '512x512',
                type: 'image/jpeg',
              },
            ]
          : [],
      });

      // Update playback state
      const state = this.getState();
      navigator.mediaSession.playbackState =
        state === PlayerState.PLAYING ? 'playing' : 'paused';
    } catch (error) {
      console.error('Failed to update media session:', error);
    }
  }

  setupMediaSessionHandlers(handlers: {
    onPlay?: () => void;
    onPause?: () => void;
    onNext?: () => void;
    onPrevious?: () => void;
    onSeek?: (time: number) => void;
  }): void {
    if (!('mediaSession' in navigator)) return;

    try {
      if (handlers.onPlay) {
        navigator.mediaSession.setActionHandler('play', handlers.onPlay);
      }
      if (handlers.onPause) {
        navigator.mediaSession.setActionHandler('pause', handlers.onPause);
      }
      if (handlers.onNext) {
        navigator.mediaSession.setActionHandler('nexttrack', handlers.onNext);
      }
      if (handlers.onPrevious) {
        navigator.mediaSession.setActionHandler('previoustrack', handlers.onPrevious);
      }
      if (handlers.onSeek) {
        navigator.mediaSession.setActionHandler('seekto', (details) => {
          if (details.seekTime !== undefined) {
            handlers.onSeek?.(details.seekTime);
          }
        });
      }

      // Set position state
      navigator.mediaSession.setActionHandler('seekbackward', () => {
        const current = this.getCurrentTime();
        this.seek(Math.max(0, current - 10));
      });

      navigator.mediaSession.setActionHandler('seekforward', () => {
        const current = this.getCurrentTime();
        const duration = this.getDuration();
        this.seek(Math.min(duration, current + 10));
      });
    } catch (error) {
      console.error('Failed to setup media session handlers:', error);
    }
  }

  updatePositionState(): void {
    if (!('mediaSession' in navigator) || !('setPositionState' in navigator.mediaSession)) {
      return;
    }

    try {
      const duration = this.getDuration();
      const position = this.getCurrentTime();

      if (duration > 0) {
        navigator.mediaSession.setPositionState({
          duration,
          position,
          playbackRate: 1,
        });
      }
    } catch (error) {
      // Ignore errors - position state is optional
    }
  }
}

// Export singleton
export const playerManager = new YouTubePlayerManager();
